import { useReusableExhibitByIdQuery } from "@codegen/index";
import { Button } from "@components/button";
import FolderIcon from "@components/icons/folderIcon";
import Spinner from "@components/spinner";
import { CheckCircle, FileText, Warning, X } from "@phosphor-icons/react";
import { api } from "@utils/api";
import { cn } from "@utils/cn";
import { useJwt } from "@utils/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { UploadFile, UploadFileStatus } from "./types";

const ReusableExhibitUploader = (props: {
  exhibitId: string;
  onDeleteFile?: (file: UploadFile) => void;
  multiple?: boolean;
  allowDelete?: boolean;
  refreshPetition: () => void;
}) => {
  const {
    multiple = false,
    allowDelete = false,
    onDeleteFile,
    exhibitId,
    refreshPetition,
  } = props;
  const token = useJwt();

  const { data, loading } = useReusableExhibitByIdQuery({
    variables: {
      id: exhibitId,
    },
  });

  const [deletingFiles, setDeletingFiles] = useState<Set<string>>(new Set());

  const handleDeleteFile = useCallback(
    async (file: UploadFile) => {
      if (onDeleteFile) {
        setDeletingFiles((prev) => {
          const newSet = new Set(prev);
          newSet.add(file.id);
          return newSet;
        });

        try {
          await onDeleteFile(file);
        } finally {
          setDeletingFiles((prev) => {
            const newSet = new Set(prev);
            newSet.delete(file.id);
            return newSet;
          });
        }
      }
    },
    [onDeleteFile]
  );

  const [fileStatus, setFileStatus] = useState<UploadFileStatus[]>();

  useEffect(() => {
    if (loading) return;
    if (
      data?.reusableExhibitById?.reusableExhibitFilesByReusableExhibitId
        ?.nodes == null
    )
      return;

    setFileStatus(
      data.reusableExhibitById.reusableExhibitFilesByReusableExhibitId.nodes?.map(
        (f) => ({
          id: f?.fileByFileId?.id ?? "",
          name: f?.fileByFileId?.name ?? "",
          status: "success",
        })
      )
    );
  }, [
    data?.reusableExhibitById?.reusableExhibitFilesByReusableExhibitId?.nodes,
    loading,
  ]);

  const uploadFile = useCallback(
    async (file: File) => {
      const uuid = Math.random().toString(36).substring(7);

      setFileStatus((prev) => [
        ...(prev ?? []),
        { file, status: "uploading", id: uuid, name: file.name },
      ]);

      // Use the same API endpoint but with a different parameter to indicate it's a reusable exhibit
      const { success } = await api.files.uploadFileToExhibit(
        file,
        exhibitId,
        token
      );

      setFileStatus((prev) =>
        (prev ?? []).map((f) => {
          if (f.id === uuid) {
            return {
              file,
              status: success ? "success" : "error",
              id: uuid,
              name: file.name,
            };
          }

          return f;
        })
      );

      // Call refreshPetition after successful upload
      if (success) {
        refreshPetition();
      }
    },
    [exhibitId, token, refreshPetition]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!multiple && (fileStatus == null || fileStatus.length > 0)) return;

      if (multiple) acceptedFiles.forEach(uploadFile);
      else uploadFile(acceptedFiles[0]);
    },
    [fileStatus, multiple, uploadFile]
  );

  const doDownload = useCallback(
    async (file: UploadFile) => {
      await api.files.downloadFile(file.id, token);
    },
    [token]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/png": [],
      "image/jpg": [],
      "image/jpeg": [],
      "image/heic": [],
      "application/pdf": [],
      "plain/text": [],
      "plain/markdown": [],
    },
  });

  const fileList = useMemo(() => {
    return (
      <>
        {(fileStatus ?? []).map((file, i) => (
          <div className="flex flex-row w-full items-center gap-x-1" key={i}>
            <div
              className={cn(
                "flex flex-row p-3 gap-x-3 justify-between w-full border rounded-sm bg-white hover:cursor-pointer hover:shadow-sm",
                file.status === "error" && "border-orange-700"
              )}
              onClick={() => doDownload(file)}
            >
              <div className="flex flex-row items-center gap-x-2">
                <FileText size={24} />
                <p className="text-sm font-medium">{file.name}</p>
              </div>
              {file.status === "uploading" && <Spinner size={24} />}
              {file.status === "success" && (
                <CheckCircle size={24} className="text-accent" />
              )}
              {file.status === "error" && (
                <Warning size={24} className="text-orange-700" />
              )}
            </div>

            {allowDelete && (
              <div
                className="flex items-center justify-center w-6 h-6"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteFile(file);
                }}
              >
                {deletingFiles.has(file.id) ? (
                  <Spinner size={16} />
                ) : (
                  <X size={16} />
                )}
              </div>
            )}
          </div>
        ))}
      </>
    );
  }, [allowDelete, doDownload, fileStatus, handleDeleteFile, deletingFiles]);

  return (
    <div className="flex flex-col w-full h-full">
      <div
        className={cn(
          "w-full h-fit bg-paleblue rounded-xl border border-dashed border-black/50",
          isDragActive && "border-accent"
        )}
      >
        <div
          className="flex flex-col items-center justify-center h-[152px] hover:cursor-pointer"
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <FolderIcon />
          <Button variant="link">
            Drag files here or upload from computer
          </Button>
          <p className="text-sm text-gray-500">
            Only .png, .jpg, .jpeg, .heic, .pdf, .txt, and .md files are
            supported
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReusableExhibitUploader;
